import type { Candle } from '../types/market.js';
import { clamp, mean } from './number.js';

export interface CandleStudy {
  direction: 'bullish' | 'bearish' | 'flat';
  shape:
    | 'strong-body'
    | 'doji-like'
    | 'lower-rejection'
    | 'upper-rejection'
    | 'indecision'
    | 'balanced';
  bodyPercent: number;
  upperWickPercent: number;
  lowerWickPercent: number;
  closeLocationPercent: number;
  volumeRatio: number;
  rawVolumeRatio: number;
  rangePercent: number;
  maturity: number;
  observations: string[];
}

export function studyCurrentCandle(candles: Candle[], maturity = 100): CandleStudy {
  const current = candles.at(-1);
  if (!current) {
    return {
      direction: 'flat',
      shape: 'balanced',
      bodyPercent: 0,
      upperWickPercent: 0,
      lowerWickPercent: 0,
      closeLocationPercent: 50,
      volumeRatio: 0,
      rawVolumeRatio: 0,
      rangePercent: 0,
      maturity,
      observations: ['No active candle is available.'],
    };
  }

  const range = Math.max(current.high - current.low, Number.EPSILON);
  const body = Math.abs(current.close - current.open);
  const upperWick = current.high - Math.max(current.open, current.close);
  const lowerWick = Math.min(current.open, current.close) - current.low;
  const bodyPercent = clamp((body / range) * 100, 0, 100);
  const upperWickPercent = clamp((upperWick / range) * 100, 0, 100);
  const lowerWickPercent = clamp((lowerWick / range) * 100, 0, 100);
  const closeLocationPercent = clamp(((current.close - current.low) / range) * 100, 0, 100);
  const history = candles.slice(-21, current.closed ? undefined : -1).filter((candle) => candle !== current);
  const averageVolume = mean(history.map((candle) => candle.volume));
  const rawVolumeRatio = averageVolume > 0 ? current.volume / averageVolume : 1;
  const elapsedFraction = current.closed ? 1 : clamp(maturity / 100, 0.05, 1);
  const volumeRatio = averageVolume > 0 ? (current.volume / elapsedFraction) / averageVolume : 1;
  const rangePercent = current.open > 0 ? (range / current.open) * 100 : 0;
  const direction = current.close > current.open ? 'bullish' : current.close < current.open ? 'bearish' : 'flat';

  let shape: CandleStudy['shape'] = 'balanced';
  if (bodyPercent < 10) shape = 'doji-like';
  else if (lowerWickPercent >= 42 && closeLocationPercent >= 60) shape = 'lower-rejection';
  else if (upperWickPercent >= 42 && closeLocationPercent <= 40) shape = 'upper-rejection';
  else if (bodyPercent >= 68) shape = 'strong-body';
  else if (upperWickPercent >= 24 && lowerWickPercent >= 24) shape = 'indecision';

  const observations: string[] = [];
  observations.push(`The active candle is ${direction} with a ${bodyPercent.toFixed(0)}% body inside its full range.`);
  if (shape === 'lower-rejection') observations.push('A long lower wick shows rejection from lower levels, but confirmation still depends on structure, timing, and flow.');
  if (shape === 'upper-rejection') observations.push('A long upper wick shows rejection from higher levels and may weaken continuation if buyers do not reclaim it.');
  if (shape === 'doji-like') observations.push('The small body shows indecision; candle direction alone is not reliable.');
  if (shape === 'strong-body') observations.push('The large body shows displacement, but historical boundaries, remaining candle life, and participation still matter.');
  if (shape === 'indecision') observations.push('Both sides produced meaningful wicks, suggesting contested control inside this candle.');
  observations.push(
    current.closed
      ? `Closed-candle volume was ${rawVolumeRatio.toFixed(2)}× the completed-candle baseline.`
      : `At ${maturity.toFixed(0)}% maturity, volume is running at an estimated ${volumeRatio.toFixed(2)}× completed-candle pace; raw accumulated volume is only ${rawVolumeRatio.toFixed(2)}× so far.`,
  );
  if (!current.closed && maturity >= 88) observations.push('The candle is near its close. Its current color is evidence about the finishing candle, not a fresh same-horizon opportunity by itself.');
  if (!current.closed && maturity <= 12) observations.push('The candle has only just opened. Early direction can be a continuation, sweep, or temporary reaction and should not be trusted alone.');
  observations.push(`The close currently sits at ${closeLocationPercent.toFixed(0)}% of the candle range.`);

  return {
    direction,
    shape,
    bodyPercent,
    upperWickPercent,
    lowerWickPercent,
    closeLocationPercent,
    volumeRatio,
    rawVolumeRatio,
    rangePercent,
    maturity,
    observations,
  };
}
